import { useState } from 'react';
import { ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getUSHolidays } from '../data/holidays';

interface OnboardingProps {
  onComplete: () => void;
  onCreateFirstCountdown: () => void;
}

export function Onboarding({ onComplete, onCreateFirstCountdown }: OnboardingProps) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [importing, setImporting] = useState(false);

  const handleCreateFirst = () => {
    setStep(2);
    onCreateFirstCountdown();
  };

  const handleImportHolidays = async () => {
    if (!user) return;

    setImporting(true);
    try {
      const holidays = getUSHolidays();
      const holidaysToInsert = holidays.map(holiday => ({
        user_id: user.id,
        name: holiday.name,
        event_date: new Date(holiday.date).toISOString(),
        emoji: holiday.emoji,
        color: holiday.color,
        category: holiday.category,
        is_recurring: holiday.is_recurring || false,
      }));

      const { error } = await supabase
        .from('countdowns')
        .insert(holidaysToInsert);

      if (error) throw error;

      setStep(3);
    } catch (error) {
      console.error('Error importing holidays:', error);
    } finally {
      setImporting(false);
    }
  };

  const handleSkipHolidays = () => {
    setStep(3);
  };

  const handleFinish = async () => {
    if (!user) return;

    try {
      await supabase.auth.updateUser({
        data: { onboarding_completed: true }
      });
    } catch (error) {
      console.error('Error updating onboarding status:', error);
    }

    onComplete();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-lg w-full p-8 animate-slideUp">
        {step === 1 && (
          <div className="text-center">
            <div className="text-6xl mb-6">👋</div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Welcome to Countdowns!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Let's get started by adding your first countdown. Track birthdays, holidays, trips, and any special moment that matters to you.
            </p>
            <button
              onClick={handleCreateFirst}
              className="w-full py-4 bg-brand-red hover:bg-brand-red/90 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2"
            >
              Create Your First Countdown
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="text-center">
            <div className="text-6xl mb-6">🗓️</div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Want to import US holidays?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              We can automatically add major US holidays to your countdowns so you never miss them.
            </p>
            <div className="space-y-3">
              <button
                onClick={handleImportHolidays}
                disabled={importing}
                className="w-full py-4 bg-brand-navy hover:bg-brand-navy/90 disabled:bg-gray-400 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    Import Holidays
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
              <button
                onClick={handleSkipHolidays}
                disabled={importing}
                className="w-full py-4 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Skip for Now
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="text-center">
            <div className="text-6xl mb-6">
              <Sparkles className="w-16 h-16 mx-auto text-yellow-500" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              You're all set! 🎉
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Your countdowns are ready. Click on any countdown card to see a live ticker with days, hours, minutes, and seconds!
            </p>
            <button
              onClick={handleFinish}
              className="w-full py-4 bg-brand-green hover:bg-brand-green/90 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2"
            >
              Add my first event
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}